import { PreviewBalloonGuideBuilder } from '../../../../../../feature/preview';
import { formatLengthMmFromMeters } from '../../../../../../utils/formatLengthMmFromMeters';
import {
  extractEdgeVertices,
  extractVertexPoint,
  resolveEdgeSelections,
} from '../../common';
import { resolveVertexShapeBySelection } from '../../topology-selection-resolution';
import {
  collectResolvedFaceFaceSharedEdges,
  filterResolvedFaceFaceSharedEdgesByHelpPoint,
  type ResolvedFaceFaceSharedEdge,
  resolveFaceFaceChamferMode,
} from '../functions/faceFaceChamferGeometry';
import {
  type ChamferGuideFaceAnchor,
  resolvePreferredChamferFaceAnchor,
} from './functions/chamferGuideFaceAnchor';
import {
  choosePreferredChamferGuideTarget,
  isSelectionLinkedToPreferredRef,
} from './functions/chamferGuideTargetMatching';
import type { ChamferKernelInstance } from '../types/ChamferShapeBuildInput';

/**
 * 倒角预览引导构建输入。
 */
export interface ChamferPreviewGuideBuildInput {
  /** Puppy CAD 内核 实例。 */
  puppy: ChamferKernelInstance;
  /** 来源实体对象 ID。 */
  sourceObjectId: string;
  /** 来源实体 Shape。 */
  sourceShape: unknown;
  /** 来源实体元数据。 */
  sourceMetadata: Record<string, unknown>;
  /** 已预处理的倒角定义。 */
  definition: ChamferFeatureDefinition;
  /** 局部预览优先引用。 */
  preferredItemRef?: string | null;
}

/**
 * 倒角预览引导边目标。
 */
interface ChamferGuideEdgeTarget {
  /** 选择项。 */
  selection: SelectionItemRef;
  /** 边端点。 */
  edgeVertices: Array<[number, number, number]>;
}

/**
 * 倒角预览气泡绑定。
 */
interface ChamferGuideBalloonBinding {
  /** 气泡源引用。 */
  sourceRef: string | null;
  /** 气泡引导线锚点。 */
  anchor: [number, number, number];
}

/**
 * Face Face 倒角预览目标。
 */
interface ChamferGuideFaceFaceTarget {
  /** 共享边端点。 */
  edgeVertices: Array<[number, number, number]>;
  /** Face Set 1 面引用。 */
  leftFaceRef: string;
  /** Face Set 2 面引用。 */
  rightFaceRef: string;
}

/**
 * 倒角预览气泡构建器。
 */
export class ChamferPreviewGuideBuilder {
  /** 全局预览气泡基础构建器。 */
  private readonly balloonGuideBuilder = new PreviewBalloonGuideBuilder({
    defaultOffsetPx: { x: 72, y: -52 },
    defaultAnchorMarker: 'dot',
  });

  /**
   * 构建倒角预览引导。
   *
   * @param input 构建输入。
   * @returns 预览引导集合。
   */
  public build(input: ChamferPreviewGuideBuildInput): BlendPreviewGuide[] {
    try {
      switch (input.definition.type) {
        case 'angle-distance':
          return this.buildAngleDistanceGuides(input);
        case 'distance-distance':
          return this.buildDistanceDistanceGuides(input);
        case 'vertex':
          return this.buildVertexGuides(input);
        case 'offset-face':
          return this.buildOffsetFaceGuides(input);
        case 'face-face':
          return this.buildFaceFaceGuides(input);
        default:
          return [];
      }
    } catch {
      /**
       * 预览实体已经由 solver 成功构建时，气泡解析失败不应反向阻断预览。
       * 这里回退为空引导，几何错误仍由 solver 主链负责抛出。
       */
      return [];
    }
  }

  /**
   * 构建角度-距离倒角气泡。
   *
   * @param input 构建输入。
   * @returns 气泡引导集合。
   */
  private buildAngleDistanceGuides(input: ChamferPreviewGuideBuildInput): BlendPreviewGuide[] {
    if (!this.isPositiveNumber(input.definition.distance) || !this.isFiniteNumber(input.definition.angleDeg)) {
      return [];
    }
    const targetEdge = this.resolveTargetEdge(input);
    if (!targetEdge) {
      return [];
    }
    const binding = this.resolveEdgeTargetBalloonBinding(input, targetEdge);
    return [this.balloonGuideBuilder.build({
      id: 'chamfer-angle-distance',
      sourceObjectId: input.sourceObjectId,
      sourceRef: binding.sourceRef,
      anchor: binding.anchor,
      fields: [
        { label: '距离', valueText: formatLengthMmFromMeters(input.definition.distance!) },
        { label: '角度', valueText: this.formatDegree(input.definition.angleDeg!) },
      ],
    })];
  }

  /**
   * 构建距离-距离倒角气泡。
   *
   * @param input 构建输入。
   * @returns 气泡引导集合。
   */
  private buildDistanceDistanceGuides(input: ChamferPreviewGuideBuildInput): BlendPreviewGuide[] {
    if (!this.isPositiveNumber(input.definition.distance)) {
      return [];
    }
    const targetEdge = this.resolveTargetEdge(input);
    if (!targetEdge) {
      return [];
    }
    const fields = input.definition.symmetric === false && this.isPositiveNumber(input.definition.distance2)
      ? [
        { label: '距离1', valueText: formatLengthMmFromMeters(input.definition.distance!) },
        { label: '距离2', valueText: formatLengthMmFromMeters(input.definition.distance2!) },
      ]
      : [
        { label: '距离', valueText: formatLengthMmFromMeters(input.definition.distance!) },
      ];
    const binding = this.resolveEdgeTargetBalloonBinding(input, targetEdge);
    return [this.balloonGuideBuilder.build({
      id: 'chamfer-distance-distance',
      sourceObjectId: input.sourceObjectId,
      sourceRef: binding.sourceRef,
      anchor: binding.anchor,
      fields,
    })];
  }

  /**
   * 构建顶点倒角气泡。
   *
   * @param input 构建输入。
   * @returns 气泡引导集合。
   */
  private buildVertexGuides(input: ChamferPreviewGuideBuildInput): BlendPreviewGuide[] {
    const vertexSelection = input.definition.items.find((item) => item.kind === 'vertex');
    if (!vertexSelection) {
      return [];
    }
    const vertexShape = resolveVertexShapeBySelection(
      input.puppy,
      input.sourceShape,
      input.sourceMetadata,
      vertexSelection,
      { sourceObjectId: input.sourceObjectId },
    );
    const vertexPoint = extractVertexPoint(input.puppy, vertexShape);
    if (!vertexPoint) {
      return [];
    }
    const fields = this.resolveVertexFields(input.definition);
    if (fields.length === 0) {
      return [];
    }
    return [this.balloonGuideBuilder.build({
      id: 'chamfer-vertex',
      sourceObjectId: input.sourceObjectId,
      sourceRef: vertexSelection.persistentRef,
      anchor: vertexPoint,
      fields,
    })];
  }

  /**
   * 解析顶点倒角气泡字段。
   *
   * @param definition 倒角定义。
   * @returns 顶点倒角气泡字段。
   */
  private resolveVertexFields(definition: ChamferFeatureDefinition) {
    if (definition.equalDistance !== false) {
      const distance = definition.distance ?? definition.vertexDistances?.[0];
      return this.isPositiveNumber(distance)
        ? [{ label: '距离', valueText: formatLengthMmFromMeters(distance!) }]
        : [];
    }
    const distances = definition.vertexDistances ?? [];
    if (distances.length !== 3 || distances.some((distance) => !this.isPositiveNumber(distance))) {
      return [];
    }
    return distances.map((distance, index) => ({
      label: `距离${index + 1}`,
      valueText: formatLengthMmFromMeters(distance),
    }));
  }

  /**
   * 构建等距面倒角气泡。
   *
   * @param input 构建输入。
   * @returns 气泡引导集合。
   */
  private buildOffsetFaceGuides(input: ChamferPreviewGuideBuildInput): BlendPreviewGuide[] {
    if (!this.isPositiveNumber(input.definition.distance)) {
      return [];
    }
    const targetEdge = this.resolveTargetEdge(input);
    if (!targetEdge) {
      return [];
    }
    const fields = input.definition.symmetric === false && this.isPositiveNumber(input.definition.distance2)
      ? [
        { label: '距离1', valueText: formatLengthMmFromMeters(input.definition.distance!) },
        { label: '距离2', valueText: formatLengthMmFromMeters(input.definition.distance2!) },
      ]
      : [
        { label: '距离', valueText: formatLengthMmFromMeters(input.definition.distance!) },
      ];
    const binding = this.resolveEdgeTargetBalloonBinding(input, targetEdge);
    return [this.balloonGuideBuilder.build({
      id: 'chamfer-offset-face',
      sourceObjectId: input.sourceObjectId,
      sourceRef: binding.sourceRef,
      anchor: binding.anchor,
      fields,
    })];
  }

  /**
   * 构建 Face Face 倒角气泡。
   *
   * @param input 构建输入。
   * @returns 气泡引导集合。
   */
  private buildFaceFaceGuides(input: ChamferPreviewGuideBuildInput): BlendPreviewGuide[] {
    const sharedEdge = this.resolveFaceFaceTargetEdge(input);
    if (!sharedEdge) {
      return [];
    }
    const fields = this.resolveFaceFaceFields(input.definition);
    if (fields.length === 0) {
      return [];
    }
    const binding = this.resolveFaceFaceBalloonBinding(input, sharedEdge);
    return [this.balloonGuideBuilder.build({
      id: 'chamfer-face-face',
      sourceObjectId: input.sourceObjectId,
      sourceRef: binding.sourceRef,
      anchor: binding.anchor,
      fields,
    })];
  }

  /**
   * 解析目标边。
   *
   * @param input 构建输入。
   * @returns 目标边；不存在时返回 null。
   */
  private resolveTargetEdge(input: ChamferPreviewGuideBuildInput): ChamferGuideEdgeTarget | null {
    const targets = resolveEdgeSelections(
      input.puppy,
      input.sourceShape,
      input.sourceMetadata,
      input.definition.items,
      { sourceObjectId: input.sourceObjectId },
    ).flatMap<ChamferGuideEdgeTarget>(({ edgeShape, selection }) => {
      const edgeVertices = extractEdgeVertices(input.puppy, edgeShape);
      if (edgeVertices.length < 2) {
        return [];
      }
      return [{
        selection,
        edgeVertices: [edgeVertices[0]!, edgeVertices[1]!],
      }];
    });
    return choosePreferredChamferGuideTarget(targets, input.preferredItemRef);
  }

  /**
   * 解析 Face Face 目标共享边。
   *
   * @param input 构建输入。
   * @returns 目标共享边；不存在时返回 null。
   */
  private resolveFaceFaceTargetEdge(input: ChamferPreviewGuideBuildInput): ChamferGuideFaceFaceTarget | null {
    const sharedEdges = collectResolvedFaceFaceSharedEdges(
      input.puppy,
      input.sourceShape,
      input.sourceMetadata,
      input.sourceObjectId,
      input.definition,
    );
    const preferredSharedEdges = this.filterFaceFaceSharedEdgesByPreferredItem(
      sharedEdges,
      input.preferredItemRef,
    );
    const [targetEdge] = filterResolvedFaceFaceSharedEdgesByHelpPoint(
      preferredSharedEdges,
      input.definition.helpPoint ?? null,
    );
    if (!targetEdge || targetEdge.vertices.length < 2) {
      return null;
    }
    return {
      edgeVertices: [targetEdge.vertices[0]!, targetEdge.vertices[1]!],
      leftFaceRef: targetEdge.leftFaceRef,
      rightFaceRef: targetEdge.rightFaceRef,
    };
  }

  /**
   * 解析边类倒角气泡绑定。
   *
   * @param input 构建输入。
   * @param targetEdge 当前气泡目标边。
   * @returns 气泡绑定。
   */
  private resolveEdgeTargetBalloonBinding(
    input: ChamferPreviewGuideBuildInput,
    targetEdge: ChamferGuideEdgeTarget,
  ): ChamferGuideBalloonBinding {
    const edgeBinding = {
      sourceRef: targetEdge.selection.persistentRef,
      anchor: this.computeEdgeMidpoint(targetEdge.edgeVertices),
    };
    if (!isSelectionLinkedToPreferredRef(targetEdge.selection, input.preferredItemRef)) {
      return edgeBinding;
    }
    const faceAnchor = this.resolvePreferredFaceAnchor(input);
    return faceAnchor ?? edgeBinding;
  }

  /**
   * 解析 Face Face 倒角气泡绑定。
   *
   * @param input 构建输入。
   * @param targetEdge 当前共享边目标。
   * @returns 气泡绑定。
   */
  private resolveFaceFaceBalloonBinding(
    input: ChamferPreviewGuideBuildInput,
    targetEdge: ChamferGuideFaceFaceTarget,
  ): ChamferGuideBalloonBinding {
    const edgeBinding = {
      sourceRef: null,
      anchor: this.computeEdgeMidpoint(targetEdge.edgeVertices),
    };
    if (
      input.preferredItemRef !== targetEdge.leftFaceRef
      && input.preferredItemRef !== targetEdge.rightFaceRef
    ) {
      return edgeBinding;
    }
    const faceAnchor = this.resolvePreferredFaceAnchor(input);
    return faceAnchor ?? edgeBinding;
  }

  /**
   * 解析当前选中面的中心锚点。
   *
   * @param input 构建输入。
   * @returns 面中心锚点。
   */
  private resolvePreferredFaceAnchor(
    input: ChamferPreviewGuideBuildInput,
  ): ChamferGuideFaceAnchor | null {
    return resolvePreferredChamferFaceAnchor({
      puppy: input.puppy,
      sourceShape: input.sourceShape,
      sourceMetadata: input.sourceMetadata,
      preferredItemRef: input.preferredItemRef,
    });
  }

  /**
   * 根据属性面板当前选中项过滤 Face Face 共享边。
   *
   * @param sharedEdges 候选共享边。
   * @param preferredItemRef 当前 UI 选中的选择项持久化引用。
   * @returns 命中当前选中面的共享边；无法命中时保持原候选集。
   */
  private filterFaceFaceSharedEdgesByPreferredItem(
    sharedEdges: ResolvedFaceFaceSharedEdge[],
    preferredItemRef?: string | null,
  ): ResolvedFaceFaceSharedEdge[] {
    if (!preferredItemRef) {
      return sharedEdges;
    }

    /**
     * Face Face 倒角的气泡锚点来自两组面的共享边。
     * 当用户在属性面板中点选某个面组项时，只收敛到与该面相关的共享边；
     * 若当前选中的是其它非面项，则不改变求解后的候选顺序。
     */
    const matchedEdges = sharedEdges.filter((sharedEdge) => (
      sharedEdge.leftFaceRef === preferredItemRef || sharedEdge.rightFaceRef === preferredItemRef
    ));

    return matchedEdges.length > 0 ? matchedEdges : sharedEdges;
  }

  /**
   * 解析 Face Face 气泡字段。
   *
   * @param definition 倒角定义。
   * @returns 字段集合。
   */
  private resolveFaceFaceFields(definition: ChamferFeatureDefinition) {
    const mode = resolveFaceFaceChamferMode(definition);
    if (mode === 'chord-width' && this.isPositiveNumber(definition.width)) {
      return [{ label: '弦宽度', valueText: formatLengthMmFromMeters(definition.width!) }];
    }
    if (mode === 'asymmetric' && this.isPositiveNumber(definition.distance) && this.isPositiveNumber(definition.distance2)) {
      return [
        { label: '距离1', valueText: formatLengthMmFromMeters(definition.distance!) },
        { label: '距离2', valueText: formatLengthMmFromMeters(definition.distance2!) },
      ];
    }
    if (this.isPositiveNumber(definition.distance)) {
      return [{ label: '距离', valueText: formatLengthMmFromMeters(definition.distance!) }];
    }
    return [];
  }

  /**
   * 判断是否为有效有限数。
   *
   * @param value 输入数值。
   * @returns 是否为有限数。
   */
  private isFiniteNumber(value: number | undefined): boolean {
    return typeof value === 'number' && Number.isFinite(value);
  }

  /**
   * 判断是否为正数。
   *
   * @param value 输入数值。
   * @returns 是否为正数。
   */
  private isPositiveNumber(value: number | undefined): boolean {
    return this.isFiniteNumber(value) && (value as number) > 0;
  }

  /**
   * 格式化角度值。
   *
   * @param value 输入数值。
   * @returns 角度文本。
   */
  private formatDegree(value: number): string {
    const rounded = Number.isInteger(value) ? value.toFixed(0) : value.toFixed(2);
    return `${rounded}度`;
  }

  /**
   * 计算边中点。
   *
   * @param edgeVertices 边端点。
   * @returns 中点。
   */
  private computeEdgeMidpoint(edgeVertices: Array<[number, number, number]>): [number, number, number] {
    return [
      (edgeVertices[0]![0] + edgeVertices[1]![0]) / 2,
      (edgeVertices[0]![1] + edgeVertices[1]![1]) / 2,
      (edgeVertices[0]![2] + edgeVertices[1]![2]) / 2,
    ];
  }
}
